import { useEffect, useRef, useState } from 'react';
import { TAGS, qk } from '@/shared/query/keys';
import { useQuery } from '@/shared/query/useQuery';
import { useStreamStatus } from '@/shared/ui/streamHealth';
import { catchUpMissedToasts, watchFrameWatermark } from './catchUp';
import { fetchUnreadCount } from './api';
import { NotificationBell } from './NotificationBell';
import { NotificationDrawer } from './NotificationDrawer';

/**
 * 降级轮询的间隔（毫秒）—— **只在实时流不可用时兜底**。
 *
 * 流正常时角标由 `notification.created` 帧驱动（帧 ⇒ 失效 `TAGS.notifications`
 * ⇒ 未读数重拉）⇒ **常态零轮询**。断流时那条链断了、角标会停在旧值 ⇒ 只能
 * 定时补一次，否则用户看到的未读数是假的。
 *
 * ⚠️ 间隔取 30s：角标是「有多急」的低价值信号，30s 内的延迟用户无感；而
 * 通知中心的历史在**行**里（不丢）⇒ 晚一点看到不影响正确性。
 */
const DEGRADED_POLL_MS = 30_000;

/**
 * 通知中心——顶栏铃铛 + 通知列表抽屉。
 *
 * **为什么是抽屉而不是路由**：
 *
 * - 铃铛是**常驻顶栏**的入口 ⇒ 点它要的是「瞄一眼」，不是「离开当前活儿」。
 *   抽屉浮在当前页面之上，操作员处理订单的途中瞄一眼通知不用换页。
 * - 路由要挂进侧栏菜单 ⇒ 得改 `ROUTE_ACCESS` / 导航结构 / 权限对齐闸，
 *   而通知是**每个登录用户**都有的入口（不是一个业务域的页面）。
 * - 通知要「**可随手关掉**」⇒ 抽屉天然做到；路由要额外做离开确认。
 */
export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const status = useStreamStatus();
  // 流不在 open ⇒ 降级 ⇒ 角标改由定时刷新兜底（见 `DEGRADED_POLL_MS`）。
  const degraded = status !== 'open';
  const count = useQuery(fetchUnreadCount, {
    key: qk.notificationsUnread(),
    tags: [TAGS.notifications],
    pollingInterval: degraded ? DEGRADED_POLL_MS : 0,
    refreshDeps: [degraded],
  });
  const unread = count.data?.count ?? 0;
  const failed = count.error !== undefined && count.error !== null;

  // 水位跟踪：订阅事件总线，记「我处理到哪一帧了」⇒ 补弹据此判「漏了哪些」。
  useEffect(() => {
    const stop = watchFrameWatermark();
    return stop;
  }, []);

  // 「连上过吗」——**第一次连上不是重连**（那时没有「断线期间」可言 ⇒ 不补弹），
  // 第二次起才是重连 ⇒ 把断线期间漏掉的 IMPORTANT 通知补弹一遍（ADR 0007 D3）。
  const everOpenRef = useRef(false);
  useEffect(() => {
    if (status !== 'open') return;
    if (!everOpenRef.current) {
      everOpenRef.current = true;
      return;
    }
    void catchUpMissedToasts().catch(() => undefined);
  }, [status]);

  return (
    <>
      <NotificationBell unread={unread} failed={failed} onOpen={() => setOpen(true)} />
      <NotificationDrawer open={open} unread={unread} onClose={() => setOpen(false)} />
    </>
  );
}
